import {
  handleValidateContact,
  handleValidateNotificationEmail,
  toOptionValueInput,
} from '@/entities/rma';
import { OTHER_RMA_REASONS, PROGRAM_REASON } from '@/shared/config';
import { useMergeState, useUpdateEffect } from '@/shared/lib/customHooks';
import { showMessageError } from '@/shared/lib';
import dayjs from 'dayjs';
import _ from 'lodash';
import { useEffect, useImperativeHandle, useRef } from 'react';
import { t } from 'ttag';
import type { NewTicketProps } from './newTicket.types';

/**
 * Backs the RMA ticket form: seeds it from the existing ticket, exposes the field handlers and publishes the validated update payload through the imperative ref. Seeding splits notificationEmail into the email input, picks the device rows, flags the Protect+ program from the Protect+ or return-maintenance reasons and records whether both contacts match; seeded values stay in a ref so later edits are diffed through updateHasChangedData.
 *
 * @param data - Existing RMA ticket the form is seeded from.
 * @param updateHasChangedData - Callback told whether the form differs from the ticket.
 * @param ref - Imperative ref exposing getUpdateData and getClinicNotes.
 * @returns Form state, refs and the field change handlers.
 */
const useNewTicket = ({ data, updateHasChangedData }: NewTicketProps, ref: any) => {
  const [state, setState] = useMergeState<any>({
    isSaving: false,
    selectedFacility: undefined,
    errorObject: {},
    errorAddress: {},
    emailInputValue: undefined,
    // data UI
    emailList: [],
    clinicNotes: undefined,
    rmaDevices: [],
    senderContact: undefined,
    recipientContact: undefined,
    isProtectPlusProgram: undefined,
  });

  const emailRef = useRef<any>(null);
  const originalData = useRef<any>({});
  const isSameAsShippingAddressRef = useRef(false);

  /**
   * Replaces the RMA ticket's notification email list with the addresses accepted by the multi-email input. Clears the email validation errors at the same time.
   *
   * @param value - Accepted notification email addresses.
   * @returns Nothing.
   */
  const onChangeEmailList = (value: any) => {
    setState({ emailList: value, errorObject: {} });
  };

  /**
   * Writes a plain text field of the RMA ticket form, such as the clinic notes, into state. The input name is used directly as the state key.
   *
   * @param name - Input name used as the state key.
   * @param value - Text value to store.
   * @returns Nothing.
   */
  const onChangeInputText = (name: any, value: any) => {
    setState({ [name]: value });
  };

  /**
   * Tracks the notification email text typed but not yet committed to the RMA ticket's email list. Clears the email validation errors on every keystroke.
   *
   * @param value - Current text in the email input.
   * @returns Nothing.
   */
  const onInputEmailChange = (value: any) => {
    setState({ emailInputValue: value, errorObject: {} });
  };

  /**
   * Converts the rows of the RMA device information form into the update payload shape and stores them as rmaDevices. Rows are written only when every row reports isComplete, reducing select options to the value and describe the API accepts, defaulting the replacement device and patient impact to null, and normalising dateOfEvent to an ISO string.
   *
   * @param deviceInfoArr - Device rows coming from the device information form.
   * @returns Nothing.
   */
  const onChangeDataDeviceRMA = (deviceInfoArr: any): any => {
    const formatDevicesInfoArray: any = [];
    if (_.every(deviceInfoArr, (item) => item.isComplete)) {
      _.forEach(deviceInfoArr, (item) => {
        if (item.isComplete) {
          // Check if all field has data
          formatDevicesInfoArray.push({
            reasons: _.map(item.reasons, toOptionValueInput),
            deviceId: item.deviceId?.value,
            replacedDevice: item.replacementDeviceId?.value || null,
            patientImpact: item.patientImpact ? toOptionValueInput(item.patientImpact) : null,
            dateOfEvent: dayjs(item.dateOfEvent).toISOString(),
            deviceModel: item.deviceModel,
            lostDeviceNeedReplacement: _.some(
              item.reasons,
              (r) => r.value === OTHER_RMA_REASONS.LOST_DEVICE,
            )
              ? item.lostDeviceNeedReplacement
              : null,
          });
        }
      });
    }
    setState({ rmaDevices: formatDevicesInfoArray });
  };

  /**
   * Stores the shipping or returning division list of the RMA ticket form. The field name from the address component is used directly as the state key.
   *
   * @param name - State key to write, shipping or returning divisions.
   * @param value - Division list to store.
   * @returns Nothing.
   */
  const onChangeDivision = (name: any, value: any) => {
    setState({ [name]: value });
  };

  /**
   * Stores a shipping or returning contact of the RMA ticket after the address form changes. The form fields are mapped onto the contact shape, with address becoming street1 and phoneNumber becoming phone, written under the given name while the address errors are cleared.
   *
   * @param name - State key to write, sender or recipient contact.
   * @param value - Address form values to convert.
   * @returns Nothing.
   */
  const onChangeDataAddress = (name: any, value: any) => {
    const { address, city, country, email, phoneNumber, state, zip, name: contactName } = value;
    const formatContact = {
      name: contactName,
      street1: address,
      city,
      country,
      state,
      zip,
      phone: phoneNumber,
      email,
    };
    setState({ [name]: formatContact, errorAddress: {} });
  };

  useEffect((): any => {
    if (data) {
      const { facility, clinicNotes, notificationEmail, senderInformation, recipientInformation } =
        data;
      const { contact: senderContact, devices } = senderInformation;
      const { contact: recipientContact } = recipientInformation;

      const orgData = {};

      const selectedFacility = {
        label: facility?.name,
        value: facility?.id,
        programs: facility?.programs,
        enabledPrograms: facility?.enabledPrograms,
        status: facility?.status,
      };

      // notification email
      const emailList = notificationEmail ? notificationEmail.split(',') : [];
      emailRef.current?.setEmailList(emailList);
      _.assign(orgData, {
        emailList,
        clinicNotes,
      });

      // senderContact,recipientContact, rmaDevices
      let isProtectPlusProgram;
      const filterDevices: any = [];
      const initialRmaDevices: any = [];
      _.forEach(devices, (device) => {
        const initialRmaDevice = {
          deviceModel: device?.device?.model,
          ..._.pick(device, [
            'deviceId',
            'dateOfEvent',
            'replacedDevice',
            'lostDeviceNeedReplacement',
          ]),
          reasons: device?.reasons ?? [],
          patientImpact: device?.patientImpact ?? null,
        };
        initialRmaDevices.push(initialRmaDevice);
        filterDevices.push({
          ...initialRmaDevice,
          reasons: _.map(initialRmaDevice.reasons, toOptionValueInput),
          patientImpact: initialRmaDevice.patientImpact
            ? toOptionValueInput(initialRmaDevice.patientImpact)
            : null,
        });

        // *: Allow facilities do not subscribe Protect+ to initiate
        if (
          _.some(device?.reasons, (reason) =>
            [PROGRAM_REASON.PROTECT_PLUS, PROGRAM_REASON.RETURN_FOR_MAINTENANCE].includes(
              reason.value,
            ),
          )
        ) {
          isProtectPlusProgram = true;
        }
      });
      _.assign(orgData, {
        senderContact,
        recipientContact,
        rmaDevices: filterDevices,
        initialRmaDevices,
      });
      originalData.current = orgData;
      isSameAsShippingAddressRef.current = _.isEqual(senderContact, recipientContact);
      setState({
        selectedFacility,
        clinicNotes,
        emailList,
        senderContact,
        recipientContact,
        rmaDevices: filterDevices,
        isProtectPlusProgram,
      });
    }
  }, [data]);

  useUpdateEffect(() => {
    const { rmaDevices, emailList, clinicNotes, senderContact, recipientContact } =
      originalData.current;
    const isChangeRmaDevices = !_.isEqual(state.rmaDevices, rmaDevices);
    const isChangeNotificationEmail = !_.isEqual(state.emailList, emailList);
    const isChangeNotes = !_.isEqual(
      _.isNil(clinicNotes) ? !!state.clinicNotes : state.clinicNotes,
      _.isNil(clinicNotes) ? !!clinicNotes : clinicNotes,
    );
    const isChangeSenderContact = !_.isEqual(state.senderContact, senderContact);
    const isChangeRecipientContact = !_.isEqual(state.recipientContact, recipientContact);
    const hasChangedData =
      isChangeRmaDevices ||
      isChangeNotificationEmail ||
      isChangeNotes ||
      isChangeSenderContact ||
      isChangeRecipientContact;
    updateHasChangedData(hasChangedData);
  }, [
    state.emailList,
    state.clinicNotes,
    state.recipientContact,
    state.senderContact,
    state.rmaDevices,
  ]);

  /**
   * Validates the RMA ticket form and builds its update payload. It checks the notification emails and the shipping contact, plus the returning contact unless it mirrors the shipping one, and requires at least one device, surfacing every problem as an error toast and returning an empty object; otherwise it returns the contacts, clinic notes and comma-joined emails, with the devices sent only when changed and stripped of deviceModel.
   *
   * @returns The ticket update payload, or an empty object when validation failed.
   */
  const validateAndGetData = async () => {
    const {
      senderContact,
      recipientContact,
      emailInputValue,
      emailList,
      shippingDivisions,
      returningDivisions,
    } = state;
    const errorObjectEmail = handleValidateNotificationEmail(emailList, emailInputValue);

    const promises = [handleValidateContact(senderContact, shippingDivisions, 'shipping')];
    if (!isSameAsShippingAddressRef.current) {
      promises.push(handleValidateContact(recipientContact, returningDivisions, 'returning'));
    }
    const [senderContactError, recipientContactError] = await Promise.all(promises);

    const invalidRmaDevice = _.isEmpty(state.rmaDevices);

    if (invalidRmaDevice) {
      showMessageError({
        content: t`Please add at least one device to be able to save a request.`,
      });
    }

    const contactError = {
      ...senderContactError,
      ...(recipientContactError || []),
    };

    if (!_.isEmpty(contactError) || !_.isEmpty(errorObjectEmail) || invalidRmaDevice) {
      setState({
        errorObject: errorObjectEmail,
        errorAddress: contactError,
      });
      _.forEach(contactError, (error) => {
        showMessageError({ content: error });
      });
      _.forEach(errorObjectEmail, (error) => {
        showMessageError({ content: error });
      });
      return {};
    }
    const isChangeRmaDevices = !_.isEqual(state.rmaDevices, originalData.current.rmaDevices);
    const clonedRmaDevices = _.cloneDeep(state.rmaDevices);
    _.forEach(clonedRmaDevices, (x) => delete x.deviceModel);
    const updateData = {
      senderContact: state.senderContact,
      recipientContact: state.recipientContact,
      rmaDevices: isChangeRmaDevices ? clonedRmaDevices : [],
      clinicNotes: state.clinicNotes,
      notificationEmail: state.emailList?.join(','),
    };
    return updateData;
  };

  /**
   * Records whether the RMA ticket's returning address mirrors its shipping address. The flag is kept in a ref so validation can skip the recipient contact without triggering a re-render.
   *
   * @param isSameAsShippingAddress - Whether both addresses are identical.
   * @returns Nothing.
   */
  const updateIsSameShippingAddress = (isSameAsShippingAddress: any) => {
    isSameAsShippingAddressRef.current = isSameAsShippingAddress;
  };

  useImperativeHandle(ref, () => ({
    getUpdateData: () => validateAndGetData(),
    getClinicNotes: () => state.clinicNotes,
  }));

  return {
    state,
    emailRef,
    originalData,
    isSameAsShippingAddressRef,
    onChangeEmailList,
    onChangeInputText,
    onInputEmailChange,
    onChangeDataDeviceRMA,
    onChangeDivision,
    onChangeDataAddress,
    updateIsSameShippingAddress,
  };
};

export { useNewTicket };
